"use client";

import { ActiveDocument, useActiveDocuments } from "@entities/common";
import { buildGovernmentEmployerDocumentRegistrationSchema, GovernmentEmployerDocumentRegistrationDto } from "@entities/employer";
import { zodResolver } from "@hookform/resolvers/zod";
import { StorageFileUploader, useStorageFile } from "@shared/storage";
import { StorageBucket, StorageFile } from "@sisnaker-react-core/karirhub-model-type";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@ubud-ui/react-ui-kit";
import { PropsWithChildren, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";

type FormProps = {
  formValue?: GovernmentEmployerDocumentRegistrationDto;
  onSubmit?: (data: GovernmentEmployerDocumentRegistrationDto) => void;
};

function getDocumentConfig(type: string) {
  return {
    bucket: StorageBucket["karirhub-employer-documents"],
    accept: type === "image" ? "image/jpeg,image/png,image/jpg" : "application/pdf",
    maxSize: 5 * 1024 * 1024,
  };
}

function createInitialFile(fileId: string): StorageFile {
  return {
    id: fileId,
    name: "File telah diunggah",
    download_link: "",
    bucket: { id: "", name: "", code: "" },
    bucket_id: "",
    user: null,
    user_id: "",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  } as StorageFile;
}

export function GovernmentEmployerDocumentRegistrationForm({ formValue, onSubmit, children }: FormProps & PropsWithChildren) {
  const { activeDocuments } = useActiveDocuments({ filters: { employerType: "government" } });

  const defaultValues = useMemo(() => {
    const files: Record<string, string> = {};

    activeDocuments.forEach((document) => {
      const existing = formValue?.documents.find((item) => item.documentId === document.id);
      files[document.id] = existing?.fileId || "";
    });

    return { files };
  }, [activeDocuments, formValue]);

  const { getFiles } = useStorageFile();
  const uploadedFiles = getFiles({
    bucket: StorageBucket["karirhub-employer-documents"],
    fileIds: formValue?.documents?.map((it) => it.fileId) || [],
  });

  const form = useForm<{ files: Record<string, string> }>({
    resolver: zodResolver(buildGovernmentEmployerDocumentRegistrationSchema(activeDocuments)),
    defaultValues,
  });

  const previousDefaultValuesRef = useRef<string>("");

  useEffect(() => {
    const serialized = JSON.stringify(defaultValues);

    if (previousDefaultValuesRef.current === serialized) {
      return;
    }

    previousDefaultValuesRef.current = serialized;
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const handleSubmit = (data: { files: Record<string, string> }) => {
    if (!onSubmit) return;

    const payload: GovernmentEmployerDocumentRegistrationDto = {
      documents: activeDocuments.map((document) => ({
        documentId: document.id,
        fileId: data.files[document.id] || "",
      })),
    };

    onSubmit(payload);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
        <div className="space-y-6">
          {activeDocuments.map((document: ActiveDocument) => {
            const config = getDocumentConfig(document.type);
            const fieldName = `files.${document.id}` as const;

            return (
              <FormField
                key={document.id}
                control={form.control}
                name={fieldName}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {document.name} {document.required && <span className="text-red-500">*</span>}
                    </FormLabel>
                    <div className="text-sm text-gray-500 mb-2">{document.description}</div>
                    <FormControl>
                      <StorageFileUploader
                        bucket={config.bucket}
                        maxFiles={1}
                        maxSize={config.maxSize}
                        accept={config.accept}
                        initialFiles={
                          field.value ? [uploadedFiles.data?.find((it) => it.id === field.value) || createInitialFile(field.value)] : []
                        }
                        onChange={({ uploadedFiles }) => {
                          field.onChange(uploadedFiles[0]?.id || "");
                        }}
                      />
                    </FormControl>
                    <FormMessage className="mt-2" />
                  </FormItem>
                )}
              />
            );
          })}
        </div>

        {children}
      </form>
    </Form>
  );
}
